import { API_URL } from "../utils/headers";

/**
 * Cliente único de la API.
 *
 * Cada servicio repetía a mano la URL, las cabeceras, el token y el manejo de
 * errores, y no todos lo hacían igual: unos lanzaban el error y otros lo
 * tragaban con `try { ... } catch { return null }`. Ese segundo estilo es el
 * que hacía que un fallo de red o un 403 se vieran en pantalla como "no hay
 * nada" en lugar de como un error, sin rastro para quien lo mirase después.
 *
 * Aquí el criterio es uno solo: si la respuesta no es correcta, se lanza un
 * Error con el detalle por campo que manda Laravel, su `status` y sus
 * `errors`, para que el formulario pueda señalar el campo concreto.
 */

const readToken = () => {
    try {
        const stored = localStorage.getItem("user");
        return stored ? JSON.parse(stored).token : null;
    } catch (error) {
        // Un localStorage corrupto no debe tumbar la aplicación entera.
        return null;
    }
};

const buildQuery = (params) => {
    if (!params) return "";

    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;
        if (Array.isArray(value)) {
            value.forEach((item) => query.append(`${key}[]`, item));
        } else {
            query.append(key, value);
        }
    });

    const queryString = query.toString();
    return queryString ? `?${queryString}` : "";
};

/**
 * Error de la API con el detalle suficiente para reaccionar.
 *
 * `status` permite distinguir un 401 (hay que volver a entrar) de un 403 (no
 * te corresponde) o de un 422 (corrige el formulario), que son tres cosas muy
 * distintas y antes llegaban todas como el mismo texto genérico de Laravel.
 */
export class ApiError extends Error {
    constructor(message, { status, errors } = {}) {
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.errors = errors ?? null;
    }
}

const parse = async (response) => {
    // 204 y respuestas vacías no traen cuerpo que interpretar.
    if (response.status === 204) return null;

    const text = await response.text();
    let payload = null;

    try {
        payload = text ? JSON.parse(text) : null;
    } catch (error) {
        payload = null;
    }

    if (!response.ok) {
        // El detalle por campo va primero: `message` es el texto genérico de
        // Laravel ("The given data was invalid") y no dice qué corregir.
        const fields = payload?.errors ? Object.values(payload.errors).flat() : [];

        throw new ApiError(
            fields.length
                ? fields.join(" ")
                : payload?.message || payload?.error || response.statusText || "Error inesperado",
            { status: response.status, errors: payload?.errors }
        );
    }

    // La API envuelve en `data`; se desenvuelve aquí para que las pantallas no
    // tengan que saberlo. Las respuestas paginadas se devuelven enteras porque
    // `pagination` va al lado de `data` y hace falta.
    if (payload && Object.prototype.hasOwnProperty.call(payload, "pagination")) {
        return payload;
    }

    return payload && Object.prototype.hasOwnProperty.call(payload, "data") ? payload.data : payload;
};

const request = async (method, path, { body, params, auth = true, raw = false, headers: extra } = {}) => {
    const headers = { Accept: "application/json" };

    if (auth) {
        const token = readToken();
        if (token) headers.Authorization = `Bearer ${token}`;
    }

    // FormData trae su propio `Content-Type` con el boundary: ponerlo a mano
    // rompe la subida.
    if (body !== undefined && !(body instanceof FormData)) {
        headers["Content-Type"] = "application/json";
    }

    const response = await fetch(`${API_URL}${path}${buildQuery(params)}`, {
        method,
        // Las cabeceras explícitas ganan: el arranque de sesión pasa su token
        // antes de que la sesión esté guardada en localStorage.
        headers: { ...headers, ...(extra ?? {}) },
        body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
    });

    return raw ? response : parse(response);
};

export const api = {
    get: (path, options) => request("GET", path, options),
    post: (path, body, options) => request("POST", path, { ...options, body }),
    put: (path, body, options) => request("PUT", path, { ...options, body }),
    patch: (path, body, options) => request("PATCH", path, { ...options, body }),
    delete: (path, options) => request("DELETE", path, options),
};
